import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AutoStoriesRoundedIcon from '@mui/icons-material/AutoStoriesRounded';
import LocalFloristRoundedIcon from '@mui/icons-material/LocalFloristRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';
import ClassStatTile from './ClassStatTile';
import WeeklyNotesTicker from './WeeklyNotesTicker';

function Cloud({ left, top, scale = 1 }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        left,
        top,
        width: 54 * scale,
        height: 22 * scale,
        borderRadius: 99,
        bgcolor: 'rgba(255,255,255,0.86)',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          bgcolor: 'inherit',
          borderRadius: '50%',
        },
        '&::before': { width: 22 * scale, height: 22 * scale, left: 8 * scale, top: -10 * scale },
        '&::after': { width: 28 * scale, height: 28 * scale, right: 7 * scale, top: -14 * scale },
      }}
    />
  );
}

function ForestIllustration({ level = 1 }) {
  const treeCount = Math.max(2, Math.min(5, Number(level) || 1));
  const trees = Array.from({ length: treeCount });
  return (
    <Box sx={{ position: 'relative', width: '100%', height: 116, overflow: 'hidden' }}>
      <Cloud left="6%" top="22%" scale={0.75} />
      <Cloud left="72%" top="20%" scale={0.6} />
      <Box sx={{ position: 'absolute', left: '18%', top: '18%', color: '#facc15', fontSize: 18, fontWeight: 1000 }}>✦</Box>
      <Box sx={{ position: 'absolute', right: '16%', top: '36%', color: '#facc15', fontSize: 14, fontWeight: 1000 }}>✦</Box>
      <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 26, bgcolor: '#3f8c23', borderRadius: '16px 16px 0 0', boxShadow: 'inset 0 5px 0 #72be44' }} />
      {trees.map((_, idx) => {
        const size = 30 + idx * 9;
        const left = 12 + idx * (72 / Math.max(1, trees.length - 1));
        return (
          <Box key={idx} sx={{ position: 'absolute', left: `${left}%`, bottom: 14, width: size, height: size + 34, transform: 'translateX(-50%)' }}>
            <Box sx={{ position: 'absolute', left: '43%', bottom: 0, width: 8 + idx, height: 34 + idx * 4, borderRadius: 99, bgcolor: '#8b4513' }} />
            <Box sx={{ position: 'absolute', left: '50%', top: 5, width: size, height: size, borderRadius: '50%', bgcolor: '#65a30d', transform: 'translateX(-50%)', boxShadow: '-9px 7px 0 #84cc16, 10px 8px 0 #4d7c0f' }} />
          </Box>
        );
      })}
    </Box>
  );
}

function LibraryIllustration() {
  return (
    <Box sx={{ position: 'relative', width: '100%', height: 116, overflow: 'hidden' }}>
      <Cloud left="70%" top="16%" scale={0.62} />
      <Box sx={{ position: 'absolute', left: '7%', top: 44, width: 62, height: 13, borderRadius: 1, bgcolor: '#f97316', boxShadow: '0 14px 0 #84cc16, 0 28px 0 #ef4444', transform: 'rotate(-13deg)' }} />
      <Box sx={{ position: 'absolute', right: '8%', top: 42, width: 64, height: 13, borderRadius: 1, bgcolor: '#2563eb', boxShadow: '0 14px 0 #f59e0b, 0 28px 0 #06b6d4', transform: 'rotate(10deg)' }} />
      <Box sx={{ position: 'absolute', left: '50%', top: 28, width: 112, height: 72, transform: 'translateX(-50%)', borderRadius: '8px 8px 16px 16px', bgcolor: '#fff7ed', boxShadow: '0 6px 0 rgba(30,64,175,0.22)' }}>
        <Box sx={{ position: 'absolute', left: '50%', top: 7, bottom: 7, width: 2, bgcolor: '#93c5fd' }} />
        {[0, 1, 2].map((idx) => (
          <Box key={idx} sx={{ position: 'absolute', left: 13, top: 18 + idx * 13, width: 34, height: 2, borderRadius: 99, bgcolor: '#fdba74' }} />
        ))}
        {[0, 1, 2].map((idx) => (
          <Box key={idx} sx={{ position: 'absolute', right: 13, top: 18 + idx * 13, width: 34, height: 2, borderRadius: 99, bgcolor: '#fdba74' }} />
        ))}
      </Box>
    </Box>
  );
}

function StarGardenIllustration() {
  return (
    <Box sx={{ position: 'relative', width: '100%', height: 116, overflow: 'hidden' }}>
      <Cloud left="62%" top="20%" scale={0.55} />
      <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 34, bgcolor: '#2f8f32', borderRadius: '18px 18px 0 0', boxShadow: 'inset 0 7px 0 #58b947' }} />
      {[17, 50, 82].map((left, idx) => (
        <Box key={left} sx={{ position: 'absolute', left: `${left}%`, top: idx === 1 ? 17 : 36, transform: 'translateX(-50%)' }}>
          <StarRoundedIcon sx={{ color: '#facc15', fontSize: idx === 1 ? 58 : 42, filter: 'drop-shadow(0 3px 0 #f59e0b)' }} />
          <Box sx={{ position: 'absolute', left: '50%', top: '43%', width: 5, height: 5, borderRadius: '50%', bgcolor: '#7c2d12', transform: 'translateX(-9px)' }} />
          <Box sx={{ position: 'absolute', left: '50%', top: '43%', width: 5, height: 5, borderRadius: '50%', bgcolor: '#7c2d12', transform: 'translateX(5px)' }} />
          <Box sx={{ position: 'absolute', left: '50%', top: '55%', width: 14, height: 6, borderBottom: '2px solid #7c2d12', borderRadius: '0 0 99px 99px', transform: 'translateX(-50%)' }} />
        </Box>
      ))}
      {[14, 33, 70, 90].map((left, idx) => (
        <Box key={left} sx={{ position: 'absolute', left: `${left}%`, bottom: 18 + (idx % 2) * 6, width: 16, height: 16, borderRadius: '50%', bgcolor: idx % 2 ? '#f472b6' : '#facc15', boxShadow: '9px 4px 0 #fff, -7px 5px 0 #fff' }} />
      ))}
    </Box>
  );
}

function RecentEvents({ events, type, children }) {
  const recent = (events || []).slice(0, 3);
  if (!recent.length) return children;
  const color = type === 'library' ? '#1e40af' : '#92400e';
  return (
    <Box sx={{ width: '100%', minWidth: 0, py: 0.5 }}>
      <Box component="ul" aria-label={type === 'library' ? 'Son kitap etkinlikleri' : 'Son yıldız etkinlikleri'}
        sx={{ listStyle: 'none', p: 0, m: 0, width: '100%', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 0.6 }}>
        {recent.map((event, index) => (
          <Box component="li" key={event.id ?? index} sx={{ width: '100%', display: 'grid', gridTemplateColumns: '24px minmax(0, 1fr)',
            gap: 0.8, alignItems: 'center', bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 1.5,
            px: 1, py: 0.7, minHeight: 36, minWidth: 0 }}>
            <Box component="span" aria-hidden="true" sx={{ width: 24, height: 24, display: 'grid', placeItems: 'center',
              color, borderRadius: '50%', bgcolor: type === 'library' ? '#dbeafe' : '#fef3c7' }}>
              {type === 'library' ? <AutoStoriesRoundedIcon sx={{ fontSize: 17 }} /> : <StarRoundedIcon sx={{ fontSize: 19 }} />}
            </Box>
            <Typography component="span" sx={{ color, fontSize: { xs: 14, md: 15 }, fontWeight: 800,
              minWidth: 0, lineHeight: 1.3, textAlign: 'left', overflowWrap: 'break-word', display: '-webkit-box', WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical', overflow: 'hidden' }} title={event.text}>{event.text}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function ClassStoryPanel({ story, onLibraryOpen, onStarGardenOpen }) {
  const forest = story?.forest || {};
  const library = story?.library || {};
  const stars = story?.stars || {};

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: '1.05fr 0.82fr 0.82fr 1.02fr',
        },
        gap: { xs: 1.3, md: 1.6 },
      }}
    >
      <ClassStatTile
        type="forest"
        icon={<LocalFloristRoundedIcon sx={{ color: '#15803d', fontSize: 28 }} />}
        title="Gelişim Ormanımız"
        metricIcon={<WaterDropRoundedIcon sx={{ fontSize: 28, color: '#0284c7' }} />}
        label={forest.label || 'Bu hafta 0 damla topladık'}
      >
        <ForestIllustration level={forest.level} />
      </ClassStatTile>

      <ClassStatTile
        type="library"
        icon={<AutoStoriesRoundedIcon sx={{ color: '#1d4ed8', fontSize: 28 }} />}
        title="Kütüphanemiz"
        metricIcon={<AutoStoriesRoundedIcon sx={{ fontSize: 27, color: '#1d4ed8' }} />}
        label={library.label || 'Sınıfımız şimdiye kadar 0 kitap bitirdi'}
        onClick={library.interaction?.enabled ? onLibraryOpen : undefined}
        interactiveLabel={library.interaction?.label}
      >
        <RecentEvents events={library.recent_events} type="library"><LibraryIllustration /></RecentEvents>
      </ClassStatTile>

      <ClassStatTile
        type="stars"
        icon={<StarRoundedIcon sx={{ color: '#f59e0b', fontSize: 30 }} />}
        title="Yıldız Bahçemiz"
        metricIcon={<StarRoundedIcon sx={{ fontSize: 28, color: '#f59e0b' }} />}
        label={stars.label || 'Güzel davranışlarımızla 0 yıldız biriktirdik'}
        onClick={stars.interaction?.enabled ? onStarGardenOpen : undefined}
        interactiveLabel={stars.interaction?.label}
      >
        <RecentEvents events={stars.recent_events} type="stars"><StarGardenIllustration /></RecentEvents>
      </ClassStatTile>

      <WeeklyNotesTicker notes={story?.weekly_notes || []} />
    </Box>
  );
}
